import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiCheckCircle, FiAlertCircle, FiInfo, FiX, FiAlertTriangle, FiLoader } from 'react-icons/fi';

export function Toast({ message, type = 'success', onClose }) {
  const configs = {
    success: { 
      icon: FiCheckCircle, 
      title: 'Success', 
      color: 'text-success', 
      bg: 'bg-surface', 
      border: 'border-success/20',
      accent: 'bg-success'
    },
    error: { 
      icon: FiAlertCircle, 
      title: 'Error', 
      color: 'text-error', 
      bg: 'bg-surface', 
      border: 'border-error/20',
      accent: 'bg-error'
    },
    warning: { 
      icon: FiAlertTriangle, 
      title: 'Warning', 
      color: 'text-warning', 
      bg: 'bg-surface', 
      border: 'border-warning/20',
      accent: 'bg-warning'
    },
    info: { 
      icon: FiInfo, 
      title: 'Info', 
      color: 'text-primary', 
      bg: 'bg-surface', 
      border: 'border-primary/20',
      accent: 'bg-primary'
    },
    loading: {
      icon: FiLoader,
      title: 'Processing',
      color: 'text-accent',
      bg: 'bg-surface',
      border: 'border-accent/20',
      accent: 'bg-accent'
    }
  };

  const theme = configs[type] || configs.success;

  React.useEffect(() => {
    if (type !== 'loading') {
      const timer = setTimeout(onClose, 5000);
      return () => clearTimeout(timer);
    }
  }, [onClose, type]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
      className={`fixed bottom-8 right-8 z-[200] flex items-center p-1 rounded-2xl shadow-card border ${theme.border} ${theme.bg} min-w-[320px] max-w-md`}
    >
      <div className={`flex-shrink-0 w-10 h-10 ${theme.accent} rounded-xl flex items-center justify-center text-white`}>
        <SafeIcon icon={theme.icon} className={`${type === 'loading' ? 'animate-spin' : ''}`} />
      </div>
      <div className="flex-grow px-4 py-3">
        <p className={`text-xs font-semibold ${theme.color}`}>
          {theme.title}
        </p>
        <p className="text-sm text-slate-700 font-medium mt-0.5">{message}</p>
      </div>
      <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
        <SafeIcon icon={FiX} />
      </button>
    </motion.div>
  );
}

export function ConfirmationModal({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Confirm', confirmVariant = 'primary', loading = false }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onCancel}
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-md"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-surface rounded-2xl shadow-2xl overflow-hidden p-8"
        >
          <div className="flex flex-col items-center text-center">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${confirmVariant === 'danger' ? 'bg-error/10 text-error' : 'bg-primary/10 text-primary'}`}>
              <SafeIcon icon={confirmVariant === 'danger' ? FiAlertTriangle : FiInfo} className="text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-ink mb-3">{title}</h3>
            <p className="text-slate-600 text-sm font-medium mb-8 leading-relaxed">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <button
                onClick={onCancel}
                disabled={loading}
                className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition-all active:scale-95 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={loading}
                className={`flex-1 py-3 text-white font-medium rounded-xl transition-all active:scale-95 disabled:opacity-50 ${
                  confirmVariant === 'danger' 
                    ? 'bg-gradient-to-r from-error to-red-600 shadow-lg shadow-error/20' 
                    : 'bg-gradient-to-r from-primary to-accent shadow-lg shadow-primary/20'
                }`}
              >
                {loading ? 'Processing...' : confirmText}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}